import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import type { ContentIdea, Draft, StoredPlan } from '@content-planner/shared';
import { api, planPdfUrl, ApiClientError } from '../lib/api.js';
import { summarizeProfile } from '../lib/labels.js';
import { weeksToText } from '../lib/planText.js';
import { WeekDisplay } from '../components/plan/WeekDisplay.js';
import { ExportActions } from '../components/plan/ExportActions.js';
import { CopyButton } from '../components/plan/CopyButton.js';

type State =
  | { status: 'loading' }
  | { status: 'not-found' }
  | { status: 'error'; message: string }
  | { status: 'loaded'; data: StoredPlan };

const PLATFORM_LABEL: Record<string, string> = {
  linkedin: 'LinkedIn',
  instagram: 'Instagram',
  facebook: 'Facebook',
};

export function ResultsPage() {
  const { planId } = useParams<{ planId: string }>();
  const [state, setState] = useState<State>({ status: 'loading' });

  // Drafts: seeded from the stored plan, then updated as the user drafts posts.
  const [drafts, setDrafts] = useState<Record<string, Draft>>({});
  const [draftingId, setDraftingId] = useState<string | null>(null);
  const [draftErrors, setDraftErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!planId) {
      setState({ status: 'not-found' });
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const data = await api.getPlan(planId);
        if (cancelled) return;
        setState({ status: 'loaded', data });
        setDrafts(data.drafts ?? {});
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiClientError && (err.status === 404 || err.status === 400)) {
          setState({ status: 'not-found' });
        } else {
          setState({
            status: 'error',
            message:
              err instanceof ApiClientError
                ? err.message
                : 'We could not load this plan. Please try again.',
          });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [planId]);

  async function handleDraft(idea: ContentIdea) {
    if (!planId || !idea.id) return;
    const ideaId = idea.id;
    setDraftingId(ideaId);
    setDraftErrors((e) => {
      const next = { ...e };
      delete next[ideaId];
      return next;
    });
    try {
      const res = await api.draft({ planId, ideaId });
      setDrafts((d) => ({ ...d, [ideaId]: res.draft }));
    } catch (err) {
      setDraftErrors((e) => ({
        ...e,
        [ideaId]:
          err instanceof ApiClientError
            ? err.message
            : 'Could not draft that post. Please try again.',
      }));
    } finally {
      setDraftingId(null);
    }
  }

  if (state.status === 'loading') {
    return (
      <div className="pt-8">
        <div className="gcm-card">
          <p className="text-text-secondary">Loading your plan...</p>
        </div>
      </div>
    );
  }

  if (state.status === 'not-found') {
    return (
      <div className="pt-8">
        <span className="gcm-eyebrow">Plan not found</span>
        <h2 className="mt-1 mb-2">We couldn't find that plan.</h2>
        <p className="text-text-secondary mb-6">
          The link may be incorrect or the plan may no longer be available. You can build a fresh
          one anytime.
        </p>
        <Link to="/plan" className="gcm-btn gcm-btn-primary">
          Build a new plan
        </Link>
      </div>
    );
  }

  if (state.status === 'error') {
    return (
      <div className="pt-8">
        <p className="text-sm text-danger">{state.message}</p>
      </div>
    );
  }

  const { plan } = state.data;
  const allWeeksText = weeksToText(plan.weeks, plan.month, drafts);

  return (
    <div className="pt-2">
      <span className="gcm-eyebrow">Your saved plan</span>
      <h2 className="mt-1 mb-1">Content plan for {plan.month}</h2>
      <p className="text-text-secondary mb-1">{summarizeProfile(plan.profile)}</p>
      <p className="text-xs text-text-muted mb-5">
        Saved — bookmark this link to come back anytime. Tap “Draft this post” on any idea to get a
        ready-to-publish caption.
      </p>

      <div className="flex flex-wrap gap-3 mb-8">
        <a
          className="gcm-btn gcm-btn-primary"
          href={planPdfUrl(state.data.planId)}
          target="_blank"
          rel="noopener noreferrer"
        >
          Download PDF
        </a>
      </div>

      {plan.weeks.map((week) => (
        <WeekDisplay
          key={week.week}
          week={week.week}
          month={plan.month}
          plan={week}
          loading={false}
          ideaSlot={(idea) => (
            <DraftSection
              idea={idea}
              draft={idea.id ? drafts[idea.id] : undefined}
              drafting={draftingId === idea.id}
              error={idea.id ? draftErrors[idea.id] : undefined}
              onDraft={() => handleDraft(idea)}
            />
          )}
        />
      ))}

      <ExportActions text={allWeeksText} />
    </div>
  );
}

function DraftSection({
  idea,
  draft,
  drafting,
  error,
  onDraft,
}: {
  idea: ContentIdea;
  draft: Draft | undefined;
  drafting: boolean;
  error: string | undefined;
  onDraft: () => void;
}) {
  const platform = PLATFORM_LABEL[idea.platform] ?? idea.platform;

  return (
    <div className="mt-4 border-t border-border-subtle pt-4">
      {!draft && (
        <button
          type="button"
          className="gcm-btn gcm-btn-secondary"
          onClick={onDraft}
          disabled={drafting}
          aria-busy={drafting}
        >
          {drafting ? 'Drafting...' : `Draft this post for ${platform}`}
        </button>
      )}

      {error && <p className="text-sm text-danger mt-3">{error}</p>}

      {draft && (
        <div className="rounded-md border border-border-subtle bg-surface-sunken/40 p-4">
          <div className="mb-2 flex items-center justify-between gap-2">
            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wide-ui bg-gcm-red text-white font-heading">
              {PLATFORM_LABEL[draft.platform] ?? draft.platform} draft
            </span>
            <div className="flex items-center gap-2">
              <CopyButton text={draft.caption} label={`Copy ${platform} caption`} />
              <button
                type="button"
                className="gcm-btn gcm-btn-ghost text-xs"
                onClick={onDraft}
                disabled={drafting}
              >
                {drafting ? 'Regenerating...' : 'Regenerate'}
              </button>
            </div>
          </div>
          <p className="text-text-primary font-body leading-relaxed whitespace-pre-line">
            {draft.caption}
          </p>
        </div>
      )}
    </div>
  );
}
